import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { getCurrentWorkOrder } from '@app/core/store/selectors/work-order.selector';
import { LoadWorkOrder } from '@dwo/actions';
import { JobFeedbackDto, JobFeedbackUpdateRequest, WorkOrderDetailsDto } from '@app/generated/model';
import { WorkOrderFeedbackService } from '@app/work-order/work-order-feedback.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { ProjectControllerService } from '@app/generated/api/projectController.service';
import { UserService } from '@app/core/services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { NoFeedbackRecipientConfirmDialogComponent } from './no-feedback-recipient-confirm-dialog.component';

@Component({
  selector: 'app-work-order-feedback',
  templateUrl: './work-order-feedback.component.html',
  styleUrls: ['./work-order-feedback.component.scss'],
  standalone: false,
})
export class WorkOrderFeedbackComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private routeWorkOrderId: string | null = null;
  private pendingLoadWorkOrderId: string | null = null;
  private initializedWorkOrderId: string | null = null;

  workOrder: WorkOrderDetailsDto | null = null;
  feedback: JobFeedbackDto[] = [];
  sentiments: JobFeedbackUpdateRequest.SentimentEnum[] = [
    JobFeedbackUpdateRequest.SentimentEnum.NotGood,
    JobFeedbackUpdateRequest.SentimentEnum.Neutral,
    JobFeedbackUpdateRequest.SentimentEnum.Good,
  ];
  impacts: JobFeedbackUpdateRequest.PotentialImpactEnum[] = [
    JobFeedbackUpdateRequest.PotentialImpactEnum.None,
    JobFeedbackUpdateRequest.PotentialImpactEnum.ScheduleProgress,
    JobFeedbackUpdateRequest.PotentialImpactEnum.Cost,
    JobFeedbackUpdateRequest.PotentialImpactEnum.Hms,
    JobFeedbackUpdateRequest.PotentialImpactEnum.Quality,
    JobFeedbackUpdateRequest.PotentialImpactEnum.Unsure,
  ];
  topics: string[] = [];
  selectedTopics = new Set<string>();
  submitting = false;
  resendingFeedbackId: string | null = null;
  recipientEmail = '';
  currentUserEmail = '';

  form = new FormGroup({
    sentiment: new FormControl<JobFeedbackUpdateRequest.SentimentEnum | null>(null, Validators.required),
    comment: new FormControl<string>(''),
    potentialImpacts: new FormControl<JobFeedbackUpdateRequest.PotentialImpactEnum[]>([], Validators.required),
    performedBy: new FormControl<string>({ value: '', disabled: true }),
    suggestedImprovement: new FormControl<string>(''),
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly store: Store,
    private readonly feedbackService: WorkOrderFeedbackService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    private readonly projectController: ProjectControllerService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    const workOrderId = (
      this.route.parent?.parent?.snapshot.paramMap.get('id') ??
      this.route.snapshot.paramMap.get('id') ??
      ''
    ).trim();

    if (!workOrderId) {
      console.warn('[JOB_FEEDBACK] Missing work order id in route.');
      return;
    }

    this.routeWorkOrderId = workOrderId;
    const targetWorkOrderId = this.routeWorkOrderId;

    this.store
      .select(getCurrentWorkOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((workOrder) => {
        if (!workOrder || workOrder.id !== targetWorkOrderId) {
          if (this.pendingLoadWorkOrderId !== targetWorkOrderId) {
            this.pendingLoadWorkOrderId = targetWorkOrderId;
            this.store.dispatch(new LoadWorkOrder(targetWorkOrderId));
          }
          return;
        }

        this.pendingLoadWorkOrderId = null;

        if (this.initializedWorkOrderId !== workOrder.id) {
          this.feedbackService.dispose();
          this.initializedWorkOrderId = workOrder.id;
          this.feedbackService.initialize(workOrder.id, workOrder);
          this.feedbackService.loadOptions();
          this.resolveRecipient(workOrder.projectId);
        }

        this.workOrder = workOrder;
      });

    this.feedbackService.feedback$.pipe(takeUntil(this.destroy$)).subscribe((feedback) => {
      this.feedback = feedback ?? [];
    });

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        const email = (user?.email ?? user?.username ?? '').toString().trim();
        this.currentUserEmail = email;
        this.form.controls.performedBy.setValue(email, { emitEvent: false });
      });

    this.feedbackService.options$.pipe(takeUntil(this.destroy$)).subscribe((options) => {
      if (!options) {
        return;
      }

      const allowedSentiments = new Set(Object.values(JobFeedbackUpdateRequest.SentimentEnum));
      const allowedImpacts = new Set(Object.values(JobFeedbackUpdateRequest.PotentialImpactEnum));

      const filteredSentiments = (options.sentiments ?? []).filter(
        (item): item is JobFeedbackUpdateRequest.SentimentEnum =>
          allowedSentiments.has(item as JobFeedbackUpdateRequest.SentimentEnum),
      );
      const filteredImpacts = (options.potentialImpacts ?? []).filter(
        (item): item is JobFeedbackUpdateRequest.PotentialImpactEnum =>
          allowedImpacts.has(item as JobFeedbackUpdateRequest.PotentialImpactEnum),
      );

      if (filteredSentiments.length) {
        this.sentiments = filteredSentiments;
      }

      if (filteredImpacts.length) {
        this.impacts = filteredImpacts;
      }

      this.topics = options.topics ?? [];
    });

    this.form.controls.sentiment.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((sentiment) => {
      const isCommentRequired =
        sentiment === JobFeedbackUpdateRequest.SentimentEnum.NotGood ||
        sentiment === JobFeedbackUpdateRequest.SentimentEnum.Neutral;

      if (isCommentRequired) {
        this.form.controls.comment.addValidators([Validators.required]);
      } else {
        this.form.controls.comment.removeValidators([Validators.required]);
      }

      this.form.controls.comment.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnDestroy(): void {
    this.feedbackService.dispose();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleTopic(topic: string, checked: boolean): void {
    if (checked) {
      this.selectedTopics.add(topic);
      return;
    }

    this.selectedTopics.delete(topic);
  }

  isTopicSelected(topic: string): boolean {
    return this.selectedTopics.has(topic);
  }

  getSentimentEmoji(sentiment: string): string {
    switch (sentiment) {
      case JobFeedbackUpdateRequest.SentimentEnum.NotGood:
        return '😟';
      case JobFeedbackUpdateRequest.SentimentEnum.Neutral:
        return '😐';
      default:
        return '😊';
    }
  }

  getTopicLabel(topic: string): string {
    const key = `workOrderFeedback.topic.${topic}`;
    const translated = this.translateService.instant(key);
    return translated === key ? topic : translated;
  }

  getSentimentLabel(sentiment: string): string {
    const key = `workOrderFeedback.sentiment.${sentiment}`;
    const translated = this.translateService.instant(key);
    return translated === key ? sentiment : translated;
  }

  getImpactLabel(impact: string): string {
    const key = `workOrderFeedback.impact.${impact}`;
    const translated = this.translateService.instant(key);
    return translated === key ? impact : translated;
  }

  isSelectedSentiment(sentiment: JobFeedbackUpdateRequest.SentimentEnum): boolean {
    return this.form.controls.sentiment.value === sentiment;
  }

  selectSentiment(sentiment: JobFeedbackUpdateRequest.SentimentEnum): void {
    this.form.controls.sentiment.setValue(sentiment);
    this.form.controls.sentiment.markAsTouched();
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const { sentiment, potentialImpacts, comment, performedBy, suggestedImprovement } = this.form.getRawValue();
    if (!sentiment || !potentialImpacts?.length) {
      this.form.markAllAsTouched();
      this.submitting = false;
      return;
    }

    const proceedWithoutRecipient = await this.confirmNoRecipientEmail();
    if (!proceedWithoutRecipient) {
      this.submitting = false;
      return;
    }

    try {
      await this.feedbackService.createFeedback({
        sentiment,
        topics: Array.from(this.selectedTopics),
        comment: comment || undefined,
        potentialImpact: potentialImpacts[0],
        potentialImpacts,
        performedBy: performedBy || undefined,
        suggestedImprovement: suggestedImprovement || undefined,
      });

      const messageKey = navigator.onLine ? 'workOrderFeedback.submitSuccess' : 'workOrderFeedback.submitQueuedOffline';
      this.snackBar.open(this.translateService.instant(messageKey), 'OK', { duration: 4000 });
      this.resetForm();
    } catch {
      this.snackBar.open(this.translateService.instant('workOrderFeedback.submitError'), 'OK', { duration: 5000 });
    } finally {
      this.submitting = false;
    }
  }

  isCommentRequired(): boolean {
    const sentiment = this.form.controls.sentiment.value;
    return (
      sentiment === JobFeedbackUpdateRequest.SentimentEnum.NotGood ||
      sentiment === JobFeedbackUpdateRequest.SentimentEnum.Neutral
    );
  }

  isPotentialImpactInvalid(): boolean {
    const control = this.form.controls.potentialImpacts;
    return control.invalid && control.touched;
  }

  hasRecipientEmail(): boolean {
    return !!this.recipientEmail?.trim();
  }

  isResending(feedbackId?: string): boolean {
    return !!feedbackId && this.resendingFeedbackId === feedbackId;
  }

  async resend(item: JobFeedbackDto): Promise<void> {
    const feedbackId = (item?.id ?? '').trim();
    if (!feedbackId || this.resendingFeedbackId) {
      return;
    }

    this.resendingFeedbackId = feedbackId;
    try {
      const proceedWithoutRecipient = await this.confirmNoRecipientEmail();
      if (!proceedWithoutRecipient) {
        return;
      }

      await this.feedbackService.resendFeedback(item);
      this.snackBar.open(this.translateService.instant('workOrderFeedback.resendSuccess'), 'OK', { duration: 4000 });
    } catch {
      this.snackBar.open(this.translateService.instant('workOrderFeedback.resendError'), 'OK', { duration: 5000 });
    } finally {
      this.resendingFeedbackId = null;
    }
  }

  private async confirmNoRecipientEmail(): Promise<boolean> {
    if (this.hasRecipientEmail()) {
      return true;
    }

    const dialogRef = this.dialog.open(NoFeedbackRecipientConfirmDialogComponent, {
      width: '95vw',
      maxWidth: '520px',
      panelClass: 'dwo-dialog',
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    return !!result;
  }

  private resolveRecipient(projectId?: string): void {
    const normalizedProjectId = (projectId ?? '').trim();
    if (!normalizedProjectId) {
      this.recipientEmail = '';
      return;
    }

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        const username = (user?.username ?? '').toString().trim();
        if (!username) {
          this.recipientEmail = '';
          return;
        }

        this.projectController
          .getAuthorizedProjects(username)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (projects) => {
              const project = (projects ?? []).find((p) => (p?.id ?? '').trim() === normalizedProjectId);
              this.recipientEmail = project?.jobFeedbackRecipientEmail ?? '';
            },
            error: () => {
              this.recipientEmail = '';
            },
          });
      });
  }

  private resetForm(): void {
    this.form.reset({
      sentiment: null,
      comment: '',
      potentialImpacts: [],
      performedBy: this.currentUserEmail,
      suggestedImprovement: '',
    });
    this.selectedTopics.clear();
  }
}
